import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useUploadAsset } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Callout,
	FormErrorMessage,
	GraphQLErrorMessage,
	ImageResizer,
	ImageUpload,
	Spinner,
} from '@treams/treams-ui';

import { IImageSpecification, ImageFile } from '../../../kakapo-ui-kit/types/Image';

export default function UserImageUpload(props: {
	userId: string;
	returnUrl?: string;
	imageSpecification: IImageSpecification;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const [uploadedFile, setUploadedFile] = useState<ImageFile | undefined>(undefined);
	const [resizedFileBlob, setResizedFileBlob] = useState<Blob | undefined>(undefined);
	const [error, setError] = useState<any | undefined>(undefined);
	const [fileTooBig, setFileTooBig] = useState(false);
	const [success, setSuccess] = useState(false);

	const [uploadAsset, { loading, error: graphQLError }] = useUploadAsset(async (assetID) => {
		if (props.imageSpecification.key === 'avatars') {
			await updateUserAvatar({
				variables: {
					input: { userID: props.userId, assetID },
				},
			});
		} else if (props.imageSpecification.key === 'banners') {
			await updateUserCover({
				variables: {
					input: { userID: props.userId, assetID },
				},
			});
		}
	});

	const [updateUserAvatar] = graphql.useUpdateUserAvatarMutation();
	const [updateUserCover] = graphql.useUpdateUserCoverMutation();

	const onFinishImage = async () => {
		if (!resizedFileBlob) {
			return;
		}

		// If selection or file is bigger than 20mb
		if (resizedFileBlob.size > 20971520) {
			setFileTooBig(true);

			return;
		}

		setFileTooBig(false);
		await uploadAsset(resizedFileBlob);
		setUploadedFile(undefined);
		setSuccess(true);

		if (props.returnUrl) {
			history.push(props.returnUrl);
		}
	};

	if (loading) {
		return <Spinner centered />;
	}

	return (
		<>
			<GraphQLErrorMessage error={graphQLError} />
			{error && <FormErrorMessage error={error} />}

			{fileTooBig && (
				<Callout mb={2} title={t('personalSettings.imageUpload.fileTooBig')} color="red" />
			)}

			{success && (
				<Callout title={t('personalSettings.imageUpload.success')} color="green" mb={2} />
			)}

			{uploadedFile == null ? (
				<ImageUpload
					accept={['image/png', 'image/jpeg', 'image/bmp']}
					translationPropertyKey={props.imageSpecification.key}
					onError={setError}
					onFileSelected={setUploadedFile}
				/>
			) : (
				<ImageResizer
					fileString={uploadedFile as string}
					imageSpecification={props.imageSpecification}
					onImageResize={setResizedFileBlob}
					onFinish={onFinishImage}
					onError={setError}
				/>
			)}
		</>
	);
}
